/**
 * The chrome's icon-only button, with the tooltip it cannot ship without.
 *
 * Every control in the toolbar is a bare glyph, so the label lives in two
 * places that must not disagree: `aria-label` for assistive tech and a hover
 * tooltip for everyone else. Baking both into one component is the same
 * reasoning as {@link ../../components/ui/row-action-icon-button.tsx} — an
 * icon-only button whose meaning is only discoverable by clicking it is not
 * finished.
 *
 * @module BrowserChromeButton
 */
import type { ReactNode } from "react";

import { cn } from "~/lib/utils";

import { Tooltip, TooltipPopup, TooltipTrigger } from "../../components/ui/tooltip";

/**
 * `size-7` rather than `size-8`: the toolbar is 40px tall, and a 32px control
 * in it leaves too little air above and below to read as a row. Defined once
 * because this exact string was previously repeated at six call sites, where it
 * had already drifted -- two of them had dropped the `disabled:` states.
 */
export const CHROME_ICON_BUTTON_CLASS =
  "inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-35";

export interface BrowserChromeIconButtonProps {
  /**
   * Accessible name AND tooltip text. Write the plain-language wording a text
   * button would have used ("Reload", "Capture screenshot") -- never abbreviate
   * it because it is normally invisible.
   */
  readonly label: string;
  readonly icon: ReactNode;
  readonly onClick: () => void;
  readonly className?: string | undefined;
  readonly disabled?: boolean | undefined;
  readonly onPointerDown?: (() => void) | undefined;
  readonly pressed?: boolean | undefined;
}

export function BrowserChromeIconButton(props: BrowserChromeIconButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            className={cn(CHROME_ICON_BUTTON_CLASS, props.className)}
            aria-label={props.label}
            aria-pressed={props.pressed}
            disabled={props.disabled ?? false}
            onPointerDown={props.onPointerDown}
            onClick={props.onClick}
          >
            {props.icon}
          </button>
        }
      />
      {/* Below: the toolbar is the top edge of the panel, so a top-side popup
          would be clipped by the panel header above it. */}
      <TooltipPopup side="bottom">{props.label}</TooltipPopup>
    </Tooltip>
  );
}
